import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";

// MUI
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import PieChart from "examples/Charts/PieChart";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

// Soft UI components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import FlagLoader from "layouts/flagloader";

import { API_URL } from "config";
import { useAuth } from "../../../AuthContext";

const parseStoredPermissions = () => {
    try {
        const stored = localStorage.getItem("permissions");
        if (!stored) return [];
        const parsed = JSON.parse(stored);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const TAB_DEFINITIONS = [
    { label: "Profile", permission: "clients.tabs.profile.view" },
    { label: "Contacts", permission: "clients.tabs.contacts.view" },
];

const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
    });
};

const getAvatarUrl = (path) => {
    if (!path || typeof path !== "string") return null;
    if (path.startsWith("http") || path.startsWith("//")) return path;
    const base = API_URL.replace(/\/api\/?$/i, "");
    let p = path.replace(/^\/+/, "");
    if (!p.startsWith("storage/")) {
        p = `storage/${p}`;
    }
    return `${base}/${p}`;
};

const displayValue = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    return String(value);
};

const booleanValue = (value) => {
    if (value === true || value === 1 || value === "1") return "Yes";
    if (value === false || value === 0 || value === "0") return "No";
    return displayValue(value);
};

const headerCellSx = {
    backgroundColor: "#f8f9fa",
    color: "#8392ab",
    fontSize: "12px",
    fontWeight: 400,
    letterSpacing: 0,
    textTransform: "capitalize",
    borderBottom: "1px solid #e9ecef",
    py: 0.75,
    px: 0.45,
    whiteSpace: "normal",
    lineHeight: 1.15,
    verticalAlign: "middle",
};

const bodyCellSx = {
    color: "#334155",
    fontSize: "10px",
    fontWeight: 400,
    borderBottom: "1px solid #edf2f7",
    py: 0.45,
    px: 0.45,
    verticalAlign: "middle",
    lineHeight: 1.15,
};

const softCardSx = {
    borderRadius: "8px",
    boxShadow: "0 12px 28px rgba(15, 23, 42, 0.06)",
    border: "1px solid rgba(226, 232, 240, 0.7)",
};

const summaryCardSx = {
    ...softCardSx,
    p: 1.25,
    minHeight: 64,
};

const tabBarSx = {
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
    p: 0.25,
    mb: 1,
    border: "1px solid #edf2f7",
    "& .MuiTabs-indicator": {
        display: "none",
    },
    "& .MuiTabs-flexContainer": {
        gap: 0.25,
        flexWrap: "wrap",
    },
    "& .MuiTab-root": {
        minHeight: 28,
        px: 1.25,
        py: 0,
        borderRadius: "6px",
        color: "#64748b",
        fontSize: "12px",
        fontWeight: 400,
        textTransform: "none",
    },
    "& .Mui-selected": {
        color: "#344767 !important",
        backgroundColor: "#fff",
        boxShadow: "0 3px 10px rgba(15, 23, 42, 0.08)",
    },
};

const avatarTileSx = {
    width: 40,
    height: 40,
    borderRadius: "8px",
    background: "linear-gradient(135deg, #21d4fd 0%, #2152ff 100%)",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.875rem",
    fontWeight: 700,
    flexShrink: 0,
};

const profileLabelSx = {
    color: "#64748b",
    fontSize: "11px",
    fontWeight: 600,
    lineHeight: 1.2,
};

const profileValueSx = {
    color: "#344767",
    fontSize: "12px",
    fontWeight: 500,
    lineHeight: 1.25,
    wordBreak: "break-word",
};

const compactButtonSx = {
    minHeight: 24,
    height: 24,
    px: 1.25,
    py: 0,
    fontSize: "10px",
    lineHeight: 1,
    textTransform: "none",
    borderRadius: "6px",
};

const cardTitleSx = {
    fontSize: "1rem",
    fontWeight: 700,
    color: "#344767",
    lineHeight: 1.2,
};

const summaryLabelSx = {
    fontSize: "11px",
    color: "#64748b",
    lineHeight: 1.2,
};

const summaryValueSx = {
    fontSize: "12px",
    fontWeight: 700,
    color: "#344767",
    lineHeight: 1.25,
};

const contactFields = [
    { label: "Name", key: "name", permission: "clients.contacts.fields.name.view" },
    { label: "Email", key: "email", permission: "clients.contacts.fields.email.view" },
    { label: "Login Email", key: "login_email", permission: "clients.contacts.fields.login_email.view" },
    { label: "Mobile", key: "mobile", permission: "clients.contacts.fields.mobile.view" },
    { label: "Login Allowed", key: "login_allowed", permission: "clients.contacts.fields.login_allowed.view", format: booleanValue },
    { label: "Status", key: "status", permission: "clients.contacts.fields.status.view" },
    { label: "Email Notifications", key: "receive_email_notifications", permission: "clients.contacts.fields.receive_email_notifications.view", format: booleanValue },
    { label: "Created At", key: "created_at", permission: "clients.contacts.fields.created_at.view", format: formatDate },
    { label: "Created By", key: "created_by_name", permission: "clients.contacts.fields.created_at.view" },
    { label: "Last Updated At", key: "updated_at", permission: "clients.contacts.fields.updated_at.view", format: formatDate },
    { label: "Last Updated By", key: "updated_by_name", permission: "clients.contacts.fields.updated_at.view" },
];

const contactSummaryKeys = ["name", "email", "mobile", "status"];

function ClientView() {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const token = localStorage.getItem("authToken");
    const { isAuthReady, permissions = [], userPermission = [] } = useAuth();

    const [client, setClient] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedContactKey, setExpandedContactKey] = useState(null);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyExporting, setHistoryExporting] = useState(false);
    const [historyError, setHistoryError] = useState("");
    const [historyRows, setHistoryRows] = useState([]);
    const [historyPagination, setHistoryPagination] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
        from: 0,
        to: 0,
    });
    const grantedPermissions = useMemo(
        () => Array.from(new Set([...permissions, ...userPermission, ...parseStoredPermissions()])),
        [permissions, userPermission]
    );
    const hasClientPermission = (...requiredPermissions) => {
        if (grantedPermissions.includes("access.system.full_access")) return true;
        return requiredPermissions.flat().filter(Boolean).some((permission) => grantedPermissions.includes(permission));
    };
    const canViewPage = hasClientPermission("clients.view_page.view");
    const canViewClientField = (field) => hasClientPermission(`clients.fields.${field}.view`);
    const canViewCard = (card) => hasClientPermission(`clients.view.cards.${card}.view`);
    const canViewChart = (chart) => hasClientPermission(`clients.view.charts.${chart}.view`);
    const visibleTabs = TAB_DEFINITIONS.filter((tab) => hasClientPermission(tab.permission));

    const query = new URLSearchParams(location.search);
    const requestedTab = query.get("tab") ? decodeURIComponent(query.get("tab")) : "Profile";
    const initialTab = visibleTabs.some((tab) => tab.label === requestedTab)
        ? requestedTab
        : visibleTabs[0]?.label || "Profile";
    const [activeTab, setActiveTab] = useState(initialTab);

    const fetchClient = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/clients/${id}`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });
            if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
            const data = await response.json();
            setClient(data.client || data.client || null);
        } catch (err) {
            setError(err.message || "Failed to load client");
        } finally {
            setLoading(false);
        }
    };

    // Chart data helpers (use API values when available, otherwise fallbacks)
    const getProjectsChart = () => {
        const total = Number(client?.total_projects) || 3;
        const completed = typeof client?.completed_projects !== "undefined" ? Number(client.completed_projects) : Math.max(1, Math.floor(total * 0.6));
        const ongoing = Math.max(0, total - completed);
        return {
            labels: ["Completed", "Ongoing"],
            datasets: {
                label: "Projects",
                data: [completed, ongoing],
                backgroundColors: ["info", "dark"],
            },
        };
    };

    const getInvoicesChart = () => {
        const due = Number(client?.due_invoices) || 1;
        const paid = Number(client?.paid_invoices) || Math.max(1, due * 2);
        return {
            labels: ["Paid", "Due"],
            datasets: {
                label: "Invoices",
                data: [paid, due],
                backgroundColors: ["success", "warning"],
            },
        };
    };

    useEffect(() => {
        if (!isAuthReady) return;
        if (!canViewPage) {
            setLoading(false);
            return;
        }
        if (id) fetchClient();
    }, [id, isAuthReady, canViewPage]);

    useEffect(() => {
        if (!visibleTabs.length) return;
        // sync active tab with URL
        const q = new URLSearchParams(location.search);
        const t = q.get("tab") ? decodeURIComponent(q.get("tab")) : "Profile";
        const nextTab = visibleTabs.some((tab) => tab.label === t) ? t : visibleTabs[0].label;
        if (nextTab !== activeTab) setActiveTab(nextTab);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search, visibleTabs.length]);

    const handleTabChange = (event, newValue) => {
        if (!visibleTabs.some((tab) => tab.label === newValue)) return;
        setActiveTab(newValue);
        navigate(`${location.pathname}?tab=${encodeURIComponent(newValue)}`);
    };

    const getContactRowKey = (contact, index) => String(contact.id || index);

    const toggleContactDetails = (contact, index) => {
        const rowKey = getContactRowKey(contact, index);
        setExpandedContactKey((currentKey) => (currentKey === rowKey ? null : rowKey));
    };

    const renderContactValue = (contact, field) => {
        const value = field.format ? field.format(contact[field.key]) : displayValue(contact[field.key]);

        if (field.key === "status") {
            const isActive = String(value).toLowerCase() === "active";

            return (
                <Chip
                    label={value}
                    size="small"
                    sx={{
                        height: 24,
                        color: isActive ? "#15803d" : "#b91c1c",
                        backgroundColor: isActive ? "#dcfce7" : "#fee2e2",
                        fontSize: "12px",
                    }}
                />
            );
        }

        return value;
    };

    const humanizeKey = (key) => String(key || "")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase());

    const formatHistoryValue = (value) => {
        if (value === null || value === undefined || value === "") return "-";
        if (Array.isArray(value)) return value.join(", ");
        if (typeof value === "object") return JSON.stringify(value);
        return String(value);
    };

    const formatHistoryChanges = (oldValues = {}, newValues = {}) => {
        const keys = Array.from(new Set([
            ...Object.keys(oldValues || {}),
            ...Object.keys(newValues || {}),
        ]));

        if (keys.length === 0) return "-";

        return keys.map((key) => `${humanizeKey(key)}: ${formatHistoryValue(oldValues?.[key])} -> ${formatHistoryValue(newValues?.[key])}`).join("; ");
    };

    const fetchHistory = async (page = 1) => {
        setHistoryLoading(true);
        setHistoryError("");

        try {
            const response = await fetch(`${API_URL}/clients/${id}/history?page=${page}&per_page=${historyPagination.per_page}`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });

            if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);

            const data = await response.json();
            const logs = data.logs || {};
            setHistoryRows(Array.isArray(logs.data) ? logs.data : []);
            setHistoryPagination({
                current_page: logs.current_page || page,
                last_page: logs.last_page || 1,
                per_page: logs.per_page || 10,
                total: logs.total || 0,
                from: logs.from || 0,
                to: logs.to || 0,
            });
        } catch (err) {
            setHistoryError(err.message || "Failed to load history");
        } finally {
            setHistoryLoading(false);
        }
    };

    const openHistory = () => {
        setHistoryOpen(true);
        fetchHistory(1);
    };

    const closeHistory = () => setHistoryOpen(false);

    const exportHistory = async () => {
        setHistoryExporting(true);

        try {
            const response = await fetch(`${API_URL}/clients/${id}/history/export`, {
                method: "GET",
                headers: { Authorization: `Bearer ${token}` },
            });

            if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `client-history-${id}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setHistoryError(err.message || "Failed to export history");
        } finally {
            setHistoryExporting(false);
        }
    };

    const renderContactsTab = () => {
        const contacts = Array.isArray(client?.contacts) ? client.contacts : [];
        const visibleContactFields = contactFields.filter((field) => hasClientPermission(field.permission));
        const summaryFields = visibleContactFields.filter((field) => contactSummaryKeys.includes(field.key));
        const detailFields = visibleContactFields.filter((field) => !contactSummaryKeys.includes(field.key));
        const hasProfilePicturePermission = hasClientPermission("clients.contacts.fields.profile_picture.view");
        const tableColumnCount = summaryFields.length + 1;

        if (contacts.length === 0) {
            return (
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Card sx={{ ...softCardSx, p: 1.25 }}>
                            <SoftTypography variant="h6" mb={0.75} sx={cardTitleSx}>Contacts</SoftTypography>
                            <SoftTypography sx={profileValueSx}>No contact details found.</SoftTypography>
                        </Card>
                    </Grid>
                </Grid>
            );
        }

        if (visibleContactFields.length === 0) {
            return (
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Card sx={{ ...softCardSx, p: 1.25 }}>
                            <SoftTypography variant="h6" mb={0.75} sx={cardTitleSx}>Contacts</SoftTypography>
                            <SoftTypography sx={profileValueSx}>No contact fields are assigned to your role.</SoftTypography>
                        </Card>
                    </Grid>
                </Grid>
            );
        }

        return (
            <Card sx={{ ...softCardSx, p: 1.25 }}>
                <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <SoftTypography variant="h6" sx={cardTitleSx}>Contacts</SoftTypography>
                    <SoftBox display="flex" alignItems="center" gap={1.5}>
                        <SoftTypography variant="body2" color="text" sx={{ fontSize: "12px" }}>
                            {contacts.length} {contacts.length === 1 ? "contact" : "contacts"}
                        </SoftTypography>
                        <SoftButton size="small" color="dark" variant="outlined" sx={compactButtonSx} onClick={openHistory}>
                            History
                        </SoftButton>
                    </SoftBox>
                </SoftBox>

                <TableContainer component={Paper} sx={{ boxShadow: "none", overflowX: "auto" }}>
                    <Table sx={{ minWidth: 900 }}>
                        <TableHead sx={{ display: "table-header-group" }}>
                            <TableRow>
                                {summaryFields.map((field) => (
                                    <TableCell key={field.key} sx={headerCellSx}>
                                        {field.label}
                                    </TableCell>
                                ))}
                                <TableCell sx={headerCellSx} align="center">Details</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {contacts.map((contact, index) => {
                                const rowKey = getContactRowKey(contact, index);
                                const isExpanded = expandedContactKey === rowKey;

                                return (
                                    <React.Fragment key={rowKey}>
                                        <TableRow hover sx={{ "&:hover": { backgroundColor: "#f8fafc" } }}>
                                            {summaryFields.map((field) => (
                                                <TableCell key={field.key} sx={bodyCellSx}>
                                                    {field.key === "name" ? (
                                                        <SoftBox display="flex" alignItems="center">
                                                            {hasProfilePicturePermission && (
                                                                <SoftBox mr={2}>
                                                                    {contact.profile_picture ? (
                                                                        <img src={getAvatarUrl(contact.profile_picture)} alt={contact.name || "Contact"} style={{ width: "28px", height: "28px", borderRadius: "50%", objectFit: "cover" }} />
                                                                    ) : (
                                                                        <SoftBox display="flex" alignItems="center" justifyContent="center" sx={{ width: 28, height: 28, borderRadius: "50%", backgroundColor: "#f1f5f9", color: "#64748b", fontSize: "10px", fontWeight: 600 }}>
                                                                            {(contact.name || `C${index + 1}`).slice(0, 1).toUpperCase()}
                                                                        </SoftBox>
                                                                    )}
                                                                </SoftBox>
                                                            )}
                                                            <SoftBox display="flex" flexDirection="column">
                                                                <SoftTypography variant="body2" color="dark" sx={{ fontSize: "10px", fontWeight: 600, lineHeight: 1.15 }}>
                                                                    {displayValue(contact.name || `Contact ${index + 1}`)}
                                                                </SoftTypography>
                                                                {hasClientPermission("clients.contacts.fields.login_email.view") && contact.login_email && (
                                                                    <SoftTypography variant="caption" color="text" sx={{ fontSize: "9px", lineHeight: 1.1 }}>{contact.login_email}</SoftTypography>
                                                                )}
                                                            </SoftBox>
                                                        </SoftBox>
                                                    ) : (
                                                        <SoftBox sx={{ wordBreak: "break-word" }}>{renderContactValue(contact, field)}</SoftBox>
                                                    )}
                                                </TableCell>
                                            ))}
                                            <TableCell sx={bodyCellSx} align="center">
                                                <Tooltip title={isExpanded ? "Hide details" : "View details"}>
                                                    <IconButton size="small" onClick={() => toggleContactDetails(contact, index)} sx={{ width: 32, height: 32, color: "#475569" }}>
                                                        {isExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell colSpan={tableColumnCount} sx={{ p: 0, borderBottom: isExpanded ? "1px solid #edf2f7" : 0 }}>
                                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                    <SoftBox sx={{ backgroundColor: "#f8fafc", px: 1.25, py: 1 }}>
                                                        <SoftBox sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", columnGap: 1.5, rowGap: 0.75 }}>
                                                            {detailFields.map((field) => (
                                                                <SoftBox key={field.key} sx={{ minWidth: 0 }}>
                                                                    <SoftTypography variant="caption" sx={profileLabelSx}>{field.label}</SoftTypography>
                                                                    <SoftTypography variant="body2" color="dark" sx={{ wordBreak: "break-word", fontSize: "10px", fontWeight: 500, lineHeight: 1.2 }}>
                                                                        {renderContactValue(contact, field)}
                                                                    </SoftTypography>
                                                                </SoftBox>
                                                            ))}
                                                        </SoftBox>
                                                    </SoftBox>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>
        );
    };

    const renderProfileRow = (field, label, valueNode) => {
        if (!canViewClientField(field)) return null;

        return (
            <Grid item xs={12}>
                <Grid container alignItems="center" sx={{ py: 0.35, borderBottom: "1px solid #f1f5f9" }}>
                    <Grid item xs={4}>
                        <SoftTypography variant="caption" sx={profileLabelSx}>{label}</SoftTypography>
                    </Grid>
                    <Grid item xs={8} sx={{ "& .MuiTypography-root": profileValueSx }}>
                        {valueNode}
                    </Grid>
                </Grid>
            </Grid>
        );
    };

    if (!isAuthReady) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <SoftBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                    <FlagLoader />
                </SoftBox>
            </DashboardLayout>
        );
    }

    if (!canViewPage) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <SoftBox px={0} pt="2px" pb={1} mt="-10px">
                    <Card sx={{ ...softCardSx, p: 1.25 }}>
                        <SoftTypography variant="h5" sx={cardTitleSx}>Clients</SoftTypography>
                        <SoftTypography variant="body2" color="text" mt={1} sx={profileValueSx}>
                            You do not have permission to view this client.
                        </SoftTypography>
                    </Card>
                </SoftBox>
                <Footer />
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <SoftBox px={0} pt="2px" pb={1} mt="-10px">
                <Card sx={{ ...softCardSx, p: 1 }}>
                    {loading ? (
                        <SoftBox display="flex" justifyContent="center" alignItems="center" height={300}>
                            <FlagLoader />
                            <SoftTypography ml={2}>Loading client...</SoftTypography>
                        </SoftBox>
                    ) : error ? (
                        <SoftBox display="flex" justifyContent="center" alignItems="center" height={300}>
                            <div>Error: {error}</div>
                        </SoftBox>
                    ) : (
                        <SoftBox p={1}>
                            {/* Tabs */}
                            <Box sx={{ mb: 1 }}>
                                <Tabs value={activeTab} onChange={handleTabChange} aria-label="client tabs" sx={tabBarSx}>
                                    {visibleTabs.map((tab) => (
                                        <Tab key={tab.label} label={tab.label} value={tab.label} />
                                    ))}
                                </Tabs>
                            </Box>

                            {/* Top cards: show only on Profile tab */}
                            {visibleTabs.length === 0 && (
                                <Card sx={{ ...softCardSx, p: 1.25 }}>
                                    <SoftTypography sx={profileValueSx}>No client view tabs are assigned to your role.</SoftTypography>
                                </Card>
                            )}

                            {activeTab === "Profile" && hasClientPermission("clients.tabs.profile.view") && (
                                <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                    {canViewCard("company") && (
                                    <Grid item xs={12} md={3}>
                                        <Card sx={summaryCardSx}>
                                            <SoftBox display="flex" alignItems="center" gap={1}>
                                                {canViewClientField("company_logo") && client?.company_logo ? (
                                                    <img src={getAvatarUrl(client.company_logo)} alt="logo" style={{ width: 40, height: 40, borderRadius: 8, objectFit: "cover" }} />
                                                ) : (
                                                    <div style={{ width: 40, height: 40, borderRadius: 8, background: "#f1f5f9" }} />
                                                )}
                                                <SoftBox>
                                                    {canViewClientField("name") && <SoftTypography sx={summaryValueSx}>{client?.name || "-"}</SoftTypography>}
                                                    {canViewClientField("company_name") && <SoftTypography variant="caption" sx={summaryLabelSx}>{client?.company_name || ""}</SoftTypography>}
                                                </SoftBox>
                                            </SoftBox>
                                        </Card>
                                    </Grid>
                                    )}

                                    <Grid item xs={12} md={9}>
                                        <Grid container spacing={1}>
                                            {canViewCard("total_projects") && (
                                            <Grid item xs={12} md={4}>
                                                <Card sx={summaryCardSx}>
                                                    <SoftTypography variant="caption" sx={summaryLabelSx}>Total Projects</SoftTypography>
                                                    <SoftTypography sx={summaryValueSx}>{client?.total_projects ?? "--"}</SoftTypography>
                                                </Card>
                                            </Grid>
                                            )}
                                            {canViewCard("total_earnings") && (
                                            <Grid item xs={12} md={4}>
                                                <Card sx={summaryCardSx}>
                                                    <SoftTypography variant="caption" sx={summaryLabelSx}>Total Earnings</SoftTypography>
                                                    <SoftTypography sx={summaryValueSx}>{client?.total_earnings ?? "--"}</SoftTypography>
                                                </Card>
                                            </Grid>
                                            )}
                                            {canViewCard("due_invoices") && (
                                            <Grid item xs={12} md={4}>
                                                <Card sx={summaryCardSx}>
                                                    <SoftTypography variant="caption" sx={summaryLabelSx}>Due Invoices</SoftTypography>
                                                    <SoftTypography sx={summaryValueSx}>{client?.due_invoices ?? "--"}</SoftTypography>
                                                </Card>
                                            </Grid>
                                            )}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )}

                            {/* Tab panels: render per activeTab */}
                            {activeTab === "Profile" && hasClientPermission("clients.tabs.profile.view") && (
                                <Grid container spacing={1}>
                                    <Grid item xs={12} md={8}>
                                        <Card sx={{ ...softCardSx, p: 1.25 }}>
                                            <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                                <SoftTypography variant="h6" sx={cardTitleSx}>Profile Info</SoftTypography>
                                                <SoftButton size="small" color="dark" variant="outlined" sx={compactButtonSx} onClick={openHistory}>
                                                    History
                                                </SoftButton>
                                            </SoftBox>
                                            <Grid container spacing={0}>
                                                {renderProfileRow("name", "Full Name", <SoftTypography>{client?.name || "-"}</SoftTypography>)}
                                                {renderProfileRow("email", "Email", <SoftTypography>{client?.email || "-"}</SoftTypography>)}
                                                {renderProfileRow("company_name", "Company Name", <SoftTypography>{client?.company_name || "-"}</SoftTypography>)}
                                                {renderProfileRow("company_logo", "Company Logo", client?.company_logo ? (
                                                    <img src={getAvatarUrl(client.company_logo)} alt="company" style={{ width: 54, height: 28, objectFit: "contain" }} />
                                                ) : (
                                                    <SoftTypography>--</SoftTypography>
                                                ))}
                                                {renderProfileRow("mobile", "Mobile", <SoftTypography>{client?.mobile || "-"}</SoftTypography>)}
                                                {renderProfileRow("office_phone_number", "Office Phone Number", <SoftTypography>{client?.office_phone_number || "-"}</SoftTypography>)}
                                                {renderProfileRow("official_website", "Official Website", <SoftTypography>{client?.official_website || "-"}</SoftTypography>)}
                                                {renderProfileRow("company_address", "Address", <SoftTypography>{client?.company_address || "-"}</SoftTypography>)}
                                                {renderProfileRow("state", "State", <SoftTypography>{client?.state || "-"}</SoftTypography>)}
                                                {renderProfileRow("city", "City", <SoftTypography>{client?.city || "-"}</SoftTypography>)}
                                                {renderProfileRow("postal_code", "Postal code", <SoftTypography>{client?.postal_code || "-"}</SoftTypography>)}
                                                {renderProfileRow("created_at", "Created At", <SoftTypography>{formatDate(client?.created_at)}</SoftTypography>)}
                                                {renderProfileRow("created_at", "Created By", <SoftTypography>{client?.created_by_name || "-"}</SoftTypography>)}
                                                {renderProfileRow("created_at", "Last Updated At", <SoftTypography>{formatDate(client?.updated_at)}</SoftTypography>)}
                                                {renderProfileRow("created_at", "Last Updated By", <SoftTypography>{client?.updated_by_name || "-"}</SoftTypography>)}
                                            </Grid>
                                        </Card>
                                    </Grid>

                                    <Grid item xs={12} md={4}>
                                        {canViewChart("projects") && (
                                            <Card sx={{ ...softCardSx, p: 1.25 }}>
                                                <SoftTypography variant="h6" mb={0.75} sx={cardTitleSx}>Projects</SoftTypography>
                                                <Box sx={{ height: 180 }}>
                                                    <PieChart chart={getProjectsChart()} height="180px" />
                                                </Box>
                                            </Card>
                                        )}

                                        {canViewChart("invoices") && (
                                            <Card sx={{ ...softCardSx, p: 1.25, mt: canViewChart("projects") ? 1 : 0 }}>
                                                <SoftTypography variant="h6" mb={0.75} sx={cardTitleSx}>Invoices</SoftTypography>
                                                <Box sx={{ height: 140 }}>
                                                    <PieChart chart={getInvoicesChart()} height="140px" />
                                                </Box>
                                            </Card>
                                        )}
                                    </Grid>
                                </Grid>
                            )}

                            {activeTab === "Contacts" && hasClientPermission("clients.tabs.contacts.view") && renderContactsTab()}
                        </SoftBox>
                    )}
                </Card>
            </SoftBox>
            <Dialog open={historyOpen} onClose={closeHistory} maxWidth="lg" fullWidth>
                <DialogTitle sx={{ py: 1 }}>
                    <SoftBox display="flex" justifyContent="space-between" alignItems="center">
                        <SoftTypography variant="h6" sx={cardTitleSx}>History</SoftTypography>
                        <SoftButton size="small" color="info" variant="gradient" sx={compactButtonSx} onClick={exportHistory} disabled={historyExporting}>
                            {historyExporting ? "Exporting..." : "Export Excel"}
                        </SoftButton>
                    </SoftBox>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 1.25 }}>
                    {historyError && (
                        <SoftTypography color="error" variant="body2" mb={2}>{historyError}</SoftTypography>
                    )}

                    <TableContainer component={Paper} sx={{ boxShadow: "none", overflowX: "auto" }}>
                        <Table sx={{ minWidth: 980 }}>
                            <TableHead sx={{ display: "table-header-group" }}>
                                <TableRow>
                                    <TableCell sx={headerCellSx}>Date & Time</TableCell>
                                    <TableCell sx={headerCellSx}>Action</TableCell>
                                    <TableCell sx={headerCellSx}>Record</TableCell>
                                    <TableCell sx={headerCellSx}>Actor</TableCell>
                                    <TableCell sx={headerCellSx}>Changes</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {historyLoading ? (
                                    <TableRow>
                                        <TableCell colSpan={5} sx={bodyCellSx}>
                                            <SoftBox display="flex" justifyContent="center" alignItems="center" minHeight={160}>
                                                <FlagLoader />
                                            </SoftBox>
                                        </TableCell>
                                    </TableRow>
                                ) : historyRows.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} sx={bodyCellSx}>
                                            <SoftBox py={3} textAlign="center">
                                                <SoftTypography variant="body2" color="text">No history found.</SoftTypography>
                                            </SoftBox>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    historyRows.map((log) => (
                                        <TableRow key={log.id} hover sx={{ "&:hover": { backgroundColor: "#f8fafc" } }}>
                                            <TableCell sx={bodyCellSx}>{formatDate(log.created_at)}</TableCell>
                                            <TableCell sx={bodyCellSx}>{humanizeKey(log.action)}</TableCell>
                                            <TableCell sx={bodyCellSx}>
                                                <SoftBox display="flex" flexDirection="column">
                                                    <SoftTypography variant="body2" color="dark" sx={{ fontSize: "10px", fontWeight: 600, lineHeight: 1.15 }}>
                                                        {log.subject_name || `#${log.subject_id}`}
                                                    </SoftTypography>
                                                    <SoftTypography variant="caption" color="text" sx={{ fontSize: "9px", lineHeight: 1.1 }}>{humanizeKey(log.subject_type)}</SoftTypography>
                                                </SoftBox>
                                            </TableCell>
                                            <TableCell sx={bodyCellSx}>
                                                <SoftBox display="flex" flexDirection="column">
                                                    <SoftTypography variant="body2" color="dark" sx={{ fontSize: "10px", fontWeight: 600, lineHeight: 1.15 }}>
                                                        {log.actor_name || "-"}
                                                    </SoftTypography>
                                                    {log.actor_email && <SoftTypography variant="caption" color="text" sx={{ fontSize: "9px", lineHeight: 1.1 }}>{log.actor_email}</SoftTypography>}
                                                </SoftBox>
                                            </TableCell>
                                            <TableCell sx={{ ...bodyCellSx, minWidth: 320 }}>
                                                <SoftTypography variant="body2" sx={{ fontSize: "10px", lineHeight: 1.25, wordBreak: "break-word" }}>
                                                    {formatHistoryChanges(log.old_values, log.new_values)}
                                                </SoftTypography>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <SoftBox display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                        <SoftTypography variant="body2" color="text" sx={{ fontSize: "12px" }}>
                            Showing {historyPagination.from || 0} to {historyPagination.to || 0} of {historyPagination.total || 0} entries
                        </SoftTypography>
                        <SoftBox display="flex" gap={1}>
                            <SoftButton size="small" color="dark" variant="outlined" sx={compactButtonSx} disabled={historyLoading || historyPagination.current_page <= 1} onClick={() => fetchHistory(historyPagination.current_page - 1)}>
                                Previous
                            </SoftButton>
                            <SoftButton size="small" color="dark" variant="outlined" sx={compactButtonSx} disabled={historyLoading || historyPagination.current_page >= historyPagination.last_page} onClick={() => fetchHistory(historyPagination.current_page + 1)}>
                                Next
                            </SoftButton>
                        </SoftBox>
                    </SoftBox>
                </DialogContent>
                <DialogActions sx={{ py: 1 }}>
                    <SoftButton color="dark" variant="outlined" sx={compactButtonSx} onClick={closeHistory}>Close</SoftButton>
                </DialogActions>
            </Dialog>
            <Footer />
        </DashboardLayout>
    );
}

export default ClientView;
